// Section « Loges » : petits outils partagés (icônes propres à La Loge, dates, tailles, libellés).
import { pad2, type ProposalStatus } from '../core/loge';
import type { MailReport } from '../data/loge';
import { icon } from './icons';

const EXTRA: Record<string, string> = {
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>',
  'folder-plus': '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/><path d="M12 10v6m-3-3h6"/>',
  lock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  unlock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 7.5-2"/>',
  file: '<path d="M6 3h8l4 4v14H6Z"/><path d="M14 3v4h4"/>',
  reply: '<path d="M10 8 5 12l5 4"/><path d="M5 12h9a5 5 0 0 1 5 5v2"/>',
  send: '<path d="m4 12 16-8-6 16-3-7Z"/><path d="m11 13 9-9"/>',
  phone: '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1Z"/>',
  note: '<path d="M5 4h14v12l-4 4H5Z"/><path d="M15 20v-4h4M9 9h6M9 13h4"/>',
  report: '<path d="M6 3h12v18H6Z"/><path d="M9 8h6M9 12h6M9 16h3"/>',
  key: '<circle cx="8" cy="15" r="4"/><path d="m11 12 9-9m-3 3 2 2m-5 1 2 2"/>',
  image: '<rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/>',
  flag: '<path d="M5 21V4"/><path d="M5 4h11l-2 4 2 4H5"/>',
  withdraw: '<path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-3"/>',
};

/** Icône de La Loge, ou celle de World à défaut. */
export function licon(name: string, cls = ''): string {
  const d = EXTRA[name];
  if (!d) return icon(name, cls);
  return `<svg class="ic${cls ? ' ' + cls : ''}" viewBox="0 0 24 24" aria-hidden="true">${d}</svg>`;
}

const dayFmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
const longFmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
const yearFmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

function parse(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/** « 14 oct. » (« 14 oct. 2025 » pour une autre année). */
export function fmtDay(iso: string | null | undefined): string {
  const t = parse(iso);
  if (t === null) return '';
  return new Date(t).getFullYear() === new Date().getFullYear() ? dayFmt.format(t) : yearFmt.format(t);
}

/** « 14 octobre ». */
export function fmtLong(iso: string | null | undefined): string {
  const t = parse(iso);
  return t === null ? '' : longFmt.format(t);
}

/** « aujourd'hui à 09:12 », « hier », sinon la date courte. */
export function fmtWhen(iso: string | null | undefined): string {
  const t = parse(iso);
  if (t === null) return '';
  const d = new Date(t), now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86_400_000);
  if (days === 0) return `aujourd’hui à ${timeFmt.format(t)}`;
  if (days === 1) return 'hier';
  return fmtDay(iso);
}

/** Taille lisible : « 38 Mo », « 512 Ko », « 1,2 Go ». */
export function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  const units = ['Ko', 'Mo', 'Go', 'To'];
  let v = bytes / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  const s = v >= 10 ? Math.round(v).toString() : (Math.round(v * 10) / 10).toString().replace('.', ',');
  return `${s} ${units[i]}`;
}

export const stepNo = (n: number): string => pad2(n);

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

/** Message après une réponse ou une demande : dit si les e-mails sont partis, sans « ok » de façade. */
export function mailToast(r: MailReport, done: string): { kind: 'info' | 'error'; text: string } {
  if (r.failed > 0) return { kind: 'error', text: `${done} dans la Loge, mais ${r.failed > 1 ? `${r.failed} e-mails ne sont pas partis` : 'l’e-mail n’est pas parti'} : vérifiez la liaison Google dans Préférences.` };
  if (r.mailed > 0) return { kind: 'info', text: `${done}, ${r.mailed > 1 ? `${r.mailed} personnes prévenues` : 'le client est prévenu'} par e-mail.` };
  return { kind: 'info', text: `${done} dans la Loge.` };
}

/** Libellé d'état d'une proposition, côté studio. */
export function proposalState(status: ProposalStatus, decidedAt: string | null): { text: string; tone: 'wait' | 'ok' | 'talk' | 'off' } {
  switch (status) {
    case 'open': return { text: 'En attente de décision', tone: 'wait' };
    case 'approved': return { text: decidedAt ? `Validée le ${fmtLong(decidedAt)}` : 'Validée', tone: 'ok' };
    case 'discussing': return { text: 'Appel demandé', tone: 'talk' };
    case 'replaced': return { text: 'Remplacée', tone: 'off' };
    case 'withdrawn': return { text: 'Retirée (le client ne la voit plus)', tone: 'off' };
  }
}
